import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Helmet } from "react-helmet";
import icon from "../assets/img/HomeBanner1.png";
import "./newhomepage.css";
import Emailjs from "emailjs-com";
import {
  FAQ,
  TICKS,
  SERVICES,
  STEPS,
  PRODUCTS,
  PROJECTS,
  PROJECT_TYPES,
  STAGES,
  STATS,
  WHY,
} from "../data";
import WhatsappButton from '../components/WhatsappButton';
import plusicon from '../assets/img/product_img/plusicon.png';
import { Link } from 'react-router-dom';


/* ---------- Shared helpers ---------- */

// className here should already include the "newhome-" prefix (e.g. "newhome-alt")
const Cta = ({ href = "#quote", children, className = "" }) => (
  <a className={`newhome-btn ${className}`} href={href}>
    {children}
  </a>
);

const c = (n) => ({ "--c": `var(--${n})` });

const Head = ({ title, children }) => (
  <div className="newhome-head">
    <h2>{title}</h2>
    {children && <p>{children}</p>}
  </div>
);

/* ---------- Hero ---------- */

const Hero = () => (
  <div className="newhome-hero">
    <div className="newhome-w">
      <div>
        <h1>Build Your Dream Entertainment Center with GEM</h1>
        <div className="newhome-lead">
          From Concept to Reality — We Design, Manufacture, Ship &amp; Install.
        </div>
        <p>
          Looking to build an indoor playground, trampoline park, or a complete
          family entertainment center? GEM delivers complete entertainment
          solutions, from customized design and in-house manufacturing to
          international shipping, professional installation, and after-sales
          support.
        </p>
        <p>
          <b>One Partner. One Complete Solution. Endless Possibilities.</b>
        </p>
        <ul className="newhome-tick">
          {TICKS.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
        <Cta>Get your free quote</Cta>
        <p className="newhome-note">
          Tell us about your project, and our team will help you turn your
          vision into reality.
        </p>
      </div>
      <img className="newhome-stack" src={icon} alt="GEM logo mark" />
    </div>
  </div>
);

/* ---------- Stats ---------- */

const Stats = () => (
  <div className="newhome-stats">
    <div className="newhome-w">
      <dl>
        {STATS.map(([col, n, label, txt]) => (
          <div style={c(col)} key={label}>
            <dt className={txt ? "newhome-txt" : undefined}>{n}</dt>
            <dd>{label}</dd>
          </div>
        ))}
      </dl>
    </div>
  </div>
);

/* ---------- Why ---------- */

const Why = () => (
  <section id="why">
    <div className="newhome-w">
      <Head title="More Than a Playground Manufacturer — Your Complete Entertainment Partner">
        At GEM, we help investors, entrepreneurs, developers, and entertainment
        operators bring their projects to life with customized entertainment
        solutions designed around their vision, space, and business needs.
      </Head>
      <div className="newhome-grid newhome-why">
        {WHY.map(([col, t, p]) => (
          <div className="newhome-it" style={c(col)} key={t}>
            <h3>{t}</h3>
            <p>{p}</p>
          </div>
        ))}
      </div>
      <div className="newhome-cta-row">
        <b>Ready to start your project?</b>
        <Cta>Discuss your project with our team</Cta>
      </div>
    </div>
  </section>
);

/* ---------- Products ---------- */

const Products = () => (
  <section id="products" className="newhome-alt-bg">
    <div className="newhome-w">
      <Head title="Complete Entertainment Equipment Solutions">
        Discover a wide range of entertainment products manufactured and
        customized to meet the needs of your project.
      </Head>
      <div className="newhome-grid">
        {PRODUCTS.map(([col, t, p, imgs, pos]) => (
          <div className="newhome-prod" style={c(col)} key={t}>
            <div className={`newhome-ph${imgs.length > 1 ? " newhome-two" : ""}`}>
              {imgs.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={imgs.length > 1 ? "" : t}
                  loading="lazy"
                  style={{ objectPosition: pos || (i ? "left" : "center") }}
                />
              ))}
            </div>
            <div className="newhome-pb">
              <h3>{t}</h3>
              <p>{p}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="newhome-cta-row">
        <Cta href="https://gemplay.com.jo/Products">Explore our products</Cta>
      </div>
    </div>
  </section>
);

/* ---------- Process ---------- */

const Process = () => (
  <section id="process">
    <div className="newhome-w">
      <Head title="From Idea to Opening Day: Your Entertainment Project. One Complete Solution.">
        Building an entertainment center involves multiple stages. GEM helps
        simplify the process by coordinating your project from the initial
        concept to installation.
      </Head>
      <ol className="newhome-steps">
        {STEPS.map(([col, t, p], i) => (
          <li style={c(col)} key={t}>
            <i>{String(i + 1).padStart(2, "0")}</i>
            <div>
              <h3>{t}</h3>
              <p>{p}</p>
            </div>
          </li>
        ))}
      </ol>
      <p className="newhome-closing">
        From the first sketch to the finished entertainment space, GEM is your
        partner throughout the journey.
      </p>
      <Cta>Start your project today</Cta>
    </div>
  </section>
);

/* ---------- International ---------- */

const Intl = () => (
  <section className="newhome-intl">
    <div className="newhome-w">
      <div>
        <h2 style={{ fontSize: "clamp(28px,3.6vw,42px)" }}>
          International Shipping &amp; Installation: From Our Factory to Your
          Entertainment Destination
        </h2>
        <p style={{ marginTop: 18 }}>
          Planning an entertainment project in Saudi Arabia, the UAE, or another
          international market?
        </p>
        <p>
          GEM provides integrated entertainment project solutions, combining
          customized design, factory manufacturing, shipping coordination, and
          installation services. Instead of coordinating multiple suppliers for
          different stages, work with one partner to simplify project planning
          and execution.
        </p>
      </div>
      <div>
        <h3 style={{ marginBottom: 16 }}>Our Services Include</h3>
        <ul>
          {SERVICES.map(([col, t]) => (
            <li style={c(col)} key={t}>
              {t}
            </li>
          ))}
        </ul>
        <p>
          Building an entertainment project abroad doesn't have to be
          complicated. Tell us your project location and requirements, and our
          team will help you explore the right solution.
        </p>
        <Cta>Request a project quotation</Cta>
      </div>
    </div>
  </section>
);

/* ---------- Projects ---------- */

const Projects = () => (
  <section id="projects">
    <div className="newhome-w">
      <Head title="Real Projects. Customized Entertainment Solutions.">
        Every project starts with an idea. We help transform that idea into an
        entertainment destination tailored to the client's vision and
        requirements.
      </Head>
      <div className="newhome-port">
        {PROJECTS.map(([src, name]) => (
          <figure key={name}>
            <img src={src} alt={`${name} project`} loading="lazy" />
            <figcaption>{name}</figcaption>
          </figure>
        ))}
      </div>
      <div className="newhome-head" style={{ marginBottom: 26 }}>
        <p style={{ margin: 0 }}>
          Explore our project portfolio to discover our designs, equipment,
          manufacturing capabilities, and completed entertainment solutions.
          From indoor playgrounds and trampoline parks to customized family
          entertainment centers, our goal is to help bring your ideas to life.
        </p>
      </div>
      <div className="newhome-cta-row">
        <b>Have a project in mind? Let's build it together.</b>
        <Cta>Explore our projects</Cta>
        <Cta className="newhome-alt">Discuss your project</Cta>
      </div>
    </div>
  </section>
);

/* ---------- FAQ ---------- */

const Faq = () => (
  <section id="faq">
    <div className="newhome-w" style={{ maxWidth: 820 }}>
      <Head title="Frequently Asked Questions" />
      {FAQ.map(([q, a]) => (
        <details key={q}>
          <summary>{q}</summary>
          <p>{a}</p>
        </details>
      ))}
    </div>
  </section>
);

/* ---------- Quote form ---------- */

const Sel = ({ name, label, opts }) => (
  <label>
    {label}
    <select name={name}>
      {opts.map((o) => (
        <option key={o}>{o}</option>
      ))}
    </select>
  </label>
);

const Quote = () => {
  // const onSubmit = (e) => {
  //   e.preventDefault();
  //   const d = new FormData(e.currentTarget);
  //   const body = [
  //     ["Name", "n"],
  //     ["Company", "c"],
  //     ["Email", "e"],
  //     ["WhatsApp", "w"],
  //     ["Country", "co"],
  //     ["Project type", "t"],
  //     ["Size", "s"],
  //     ["Stage", "p"],
  //   ]
  //     .map(([k, f]) => `${k}: ${d.get(f) || ""}`)
  //     .join("\n");
  //   // Replace with a fetch() to your backend / form service to submit without opening the mail app.
  //   window.location.href = `mailto:info@gemplay.com.jo?subject=${encodeURIComponent("Project inquiry")}&body=${encodeURIComponent(body)}`;
  // };

  
const onSubmit = async (e) => {
  e.preventDefault();

  const form = e.currentTarget;
  const d = new FormData(form);

  const templateParams = {
    name: d.get("n") || "",
    company: d.get("c") || "",
    email: d.get("e") || "",
    whatsapp: d.get("w") || "",
    country: d.get("co") || "",
    project_type: d.get("t") || "",
    project_size: d.get("s") || "",
    project_stage: d.get("p") || "",
  };

  try {
    const response = await Emailjs.send(
      "service_3f6cyw8",
      "template_1sjslzh",
      templateParams,
      "u3W-si69qTqJUjdDm"
    );

    console.log("Email sent successfully:", response);

    alert("Your project inquiry has been submitted successfully!");

    form.reset();
  } catch (error) {
    console.error("Email sending failed:", error);

    alert(
      "Unable to submit your inquiry. Please try again later."
    );
  }
};


  return (
    <section id="quote" className="newhome-alt-bg">
      <div className="newhome-w newhome-quote">
        <aside>
          <h2
            style={{ fontSize: "clamp(28px,3.6vw,42px)", color: "var(--navy)" }}
          >
            Let's Build Your Entertainment Project
          </h2>
          <p style={{ marginTop: 14 }}>
            Tell us a little about your project, and our team will contact you
            to discuss your requirements.
          </p>
          <b>Your project starts with a conversation. Let's make it happen.</b>
        </aside>
        <form onSubmit={onSubmit}>
          <h3 className="newhome-full">Project Inquiry Form</h3>
          <label>
            Full Name
            <input name="n" required autoComplete="name" />
          </label>
          <label>
            Company Name
            <input name="c" autoComplete="organization" />
          </label>
          <label>
            Business Email
            <input name="e" type="email" required autoComplete="email" />
          </label>
          <label>
            WhatsApp Number
            <input name="w" type="tel" autoComplete="tel" />
          </label>
          <label>
            Country
            <input name="co" required autoComplete="country-name" />
          </label>
          <Sel name="t" label="Project Type" opts={PROJECT_TYPES} />
          <label>
            Estimated Project Size
            <input name="s" placeholder="e.g. 1,000 m²" />
          </label>
          <Sel name="p" label="Project Stage" opts={STAGES} />
          <div className="newhome-full">
            <button className="newhome-btn" type="submit">
              Get my project quote
            </button>
            <p className="newhome-note">
              Our team will review your requirements and contact you to discuss
              the next steps.
            </p>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ---------- Final CTA ---------- */

const Final = () => (
  <section className="newhome-final">
    <div className="newhome-bar">
      <span style={{ background: "var(--yel)" }} />
      <span style={{ background: "#8fd3ec" }} />
      <span style={{ background: "var(--cyan)" }} />
      <span style={{ background: "var(--mag)" }} />
    </div>
    <div className="newhome-w">
      <h2>Your Next Entertainment Project Starts Here.</h2>
      <p>
        Whether you're opening your first indoor playground or expanding an
        established entertainment business, GEM is ready to help you explore the
        right solution.
      </p>
      <div className="newhome-tag">
        DESIGN. MANUFACTURE. SHIP. INSTALL. SUPPORT.
      </div>
      <p>
        One partner to help bring your entertainment vision to life. Let's
        discuss your project and create a solution tailored to your business.
      </p>
      <Cta>Request your quote today</Cta>
      <p style={{ fontSize: 15 }}>
        Contact our team to discuss your project requirements.
      </p>
    </div>
  </section>
);

/* ---------- Page ---------- */

export default function NewHomePage() {
  return (
    <>
      <Helmet>
        <title>Best Indoor Playground Equipment Manufacturer in Jordan</title>
        <meta
          name="description"
          content="Gemplay: global manufacturer of indoor playgrounds, soft play, trampoline parks, ninja courses & FEC equipment. Top-quality design and installation worldwide."
        />

        {/* Open Graph Meta */}
        <meta
          property="og:title"
          content="Best Indoor Playground Equipment Manufacturer in Jordan"
        />
        <meta
          property="og:description"
          content="Gemplay: global manufacturer of indoor playgrounds, soft play, trampoline parks, ninja courses & FEC equipment. Top-quality design and installation worldwide."
        />
        <meta property="og:image" content="https://gemplay.com.jo/gemIcon.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://gemplay.com.jo/" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://gemplay.com.jo/gemIcon.jpg" />
      </Helmet>

      <Header />

      {/* All new-homepage styles are scoped to this wrapper */}
      <div className="newhome-page">
        <Hero />
        <Stats />
        <Why />
        <Products />
        <Process />
        <Intl />
        <Projects />
        <Faq />
        <Quote />
        <Final />
        <WhatsappButton
                  altText="Chat with us on WhatsApp"
                  iconSize={70}
                />
                  <Link to="/getquotations" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div className="icon-container">
                      <img
                        src={plusicon}
                        alt="commercial indoor playground equipment"
                        className="Producticon"
                        style={{ cursor: "pointer" }}
                      />
                      <button className="quotation-button">Get Quotation</button>
                    </div>
                  </Link>
      </div>

      <Footer />
    </>
  );
}